<template>
  <div>
    <ModalDialog :closeable="$t('common.close')" :open.sync="isFirstModalOpen">
      <h2>{{ $t('workFileTracking.cancelModal.title') }}</h2>
      <p>{{ $t('workFileTracking.cancelModal.firstModal.p1') }}</p>
      <p>{{ $t('workFileTracking.cancelModal.firstModal.p2') }}</p>
      <p>{{ $t('workFileTracking.cancelModal.firstModal.p3') }}</p>
      <p v-html="$t('workFileTracking.cancelModal.firstModal.p4')"></p>
      <div class="row">
        <div class="col-xs-12 col-sm-5 col-md-5 col-sm-offset-1 col-md-offset-1">
          <a @click="cancelWork" class="lib__btn lib__btn--outline lib__btn--dark lib__btn--rounded" href="#" type="button">
            <span>{{ $t('workFileTracking.tracking.cancelWork') }}</span>
          </a>
        </div>
        <div class="col-xs-12 col-sm-5 col-md-5 col-sm-offset-1 col-md-offset-1">
          <a @click="keepAppointment" class="lib__btn lib__btn--primary lib__btn--rounded" href="#" type="button">
            <span>{{ $t('workFileTracking.tracking.keepAppointment') }}</span>
          </a>
        </div>
      </div>
    </ModalDialog>

    <ModalDialog :closeable="$t('common.close')" :open.sync="isSecondModalOpen">
      <h2>{{ $t('workFileTracking.cancelModal.title') }}</h2>
      <p v-html="$t('workFileTracking.cancelModal.secondModal.p1')"></p>
      <div class="row">
        <div class="col-xs-12 col-sm-5 col-md-5 col-sm-offset-1 col-md-offset-1">
          <a @click="cancelWorkConfirmation" :class="{ 'lib__btn--disabled': loading }" class="lib__btn lib__btn--outline lib__btn--dark lib__btn--rounded" href="#" type="button">
            <span>{{ $t('workFileTracking.tracking.cancelWork') }}</span>
          </a>
        </div>
        <div class="col-xs-12 col-sm-5 col-md-5 col-sm-offset-1 col-md-offset-1">
          <a @click="keepAppointment" :class="{ 'lib__btn--disabled': loading }" class="lib__btn lib__btn--primary lib__btn--rounded" href="#" type="button">
            <span>{{ $t('workFileTracking.tracking.keepAppointment') }}</span>
          </a>
        </div>
      </div>
    </ModalDialog>

    <ModalDialog :closeable="$t('common.close')" @onClose="onConfirmationModalClose" :open.sync="isConfirmationModalOpen">
      <h2>{{ $t('workFileTracking.cancelModal.title') }}</h2>
      <p>{{ $t('workFileTracking.cancelModal.confirmationModal.p1') }}</p>
    </ModalDialog>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import { WorkFile, WorkFileCancellationRequest, WorkFileIdentification } from '@/models'
import { appointmentService } from '@/services/appointment-service'
import { State } from 'vuex-class'

@Component
export default class CancelWorkModal extends Vue {
  //#region [Property]
  @Prop({ type: Object, required: true }) public readonly workFile!: WorkFile
  @Prop({ type: Boolean, required: true }) public readonly isModalOpen!: boolean
  //#endregion

  //#region [Data]
  @State('identification', { namespace: 'workFileTracking/identification' })
  public identificationState!: WorkFileIdentification | null

  public isSecondModalOpen: boolean = false
  public isConfirmationModalOpen: boolean = false

  public loading: boolean = false
  //#endregion

  //#region [Computed]
  public get isFirstModalOpen(): boolean {
    return this.isModalOpen
  }

  public set isFirstModalOpen(val: boolean) {
    this.$emit('close', val)
  }
  //#endregion

  //#region [Method]
  public cancelWork() {
    this.isSecondModalOpen = true
    this.isFirstModalOpen = false
  }

  public keepAppointment() {
    if (this.loading) return
    this.isSecondModalOpen = false
    this.isFirstModalOpen = false
  }

  public async cancelWorkConfirmation() {
    if (this.loading) return
    try {
      this.loading = true
      this.$appInsights.trackEvent('WorkFileTracking_CancelWork_Cancelled', {
        Segment: 'WorkFileTracking',
        ZipCode: this.identificationState!.zipCode!.trim(),
        WorksFileId: this.identificationState!.worksFileId!.trim(),
        WorkRequestCode: this.workFile.workRequests[0].code
      })

      const token = sessionStorage.getItem('token')
      const data: WorkFileCancellationRequest = {
        workFileReference: this.workFile.reference,
        powerLevel: appointmentService.appointmentPowerLevel(this.workFile),
        workRequestType: appointmentService.appointmentWorkRequestType(this.workFile),
        service: appointmentService.appointmentWorkRequestService(this.workFile),
        topic: appointmentService.appointmentWorkRequestTopic(this.workFile),
        language: 'fr', // lang will be determined in API but cannot be null
        contactInfo: this.workFile.contactInfo ?? {
          name: null,
          mobilePhoneNumber: null,
          housePhoneNumber: null,
          email: null
        }
      }

      if (!token) {
        throw new Error('No token found in sessionStorage');
      }

      const response = await this.$api
        .post(`api/WorksRequest/work-file/${this.workFile.reference}/close`, data, {
          headers: {
            Authorization: token
          }
        })
        .then(() => {
          this.isSecondModalOpen = false
          this.isFirstModalOpen = false
          this.isConfirmationModalOpen = true
        })
        .catch(() => {
          this.isSecondModalOpen = false
          this.isFirstModalOpen = false
        })
    } finally {
      this.loading = false
    }

  }

  public onConfirmationModalClose() {
    location.href = this.$t('common.oresLink').toString()
  }
  //#endregion
}
</script>